import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, Paper, Grid, Skeleton, Card } from "@mui/material";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { Bar } from 'react-chartjs-2';
import axios from "axios";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { authState, popupState } from "../../Recoil/RecoilState";
import { chartColors, baseURL } from "../../utils/StaticVariables";
import DesBtn from "../Reusable/DesBtn";
import OrderFilter from "./OrderFilter";

const commonOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'top', labels: { color: 'gray' } },
    title: { display: false }
  },
  scales: {
    x: { ticks: { color: 'gray' } },
    y: { ticks: { color: 'gray' } }
  }
};

const HighValueChart = () => {
  const [dataList, setDataList] = useState(null);
  const [loading, setLoading] = useState(false);
  const { token } = useRecoilValue(authState);
  const setPopup = useSetRecoilState(popupState);
  const childRef = useRef(null);
  const [filter, setFilter] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = { ...filter };
        if (params.locations) { params.location = params.locations; delete params.locations; }
        if (params.buildings) { params.building = params.buildings; delete params.buildings; }
        if (params.zones) { params.zone = params.zones; delete params.zones; }

        const { data } = await axios.get(`${baseURL}shoplifting/dashboard/high-value-analysis`, {
          headers: { Authorization: `Bearer ${token}` },
          params
        });
        setDataList(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchData();
  }, [filter, token]);

  const openFilter = () => {
    setPopup({
      isOpen: true,
      title: "Select Date, Time and Location Filters",
      content: (
        <OrderFilter ref={childRef} filter={filter} changeFilterHandle={setFilter} />
      ),
      sendReq: () => childRef.current?.submit(),
    });
  };

  const total_estimated_value = dataList?.total_estimated_value || [];
  const items_stolen = dataList?.items_stolen || [];

  const valueDates = Array.from(new Set(total_estimated_value.map(item => item.date || item.day || item.timestamp))).filter(Boolean).sort();
  const severities = Array.from(new Set(total_estimated_value.map(item => item.severity || item.threat_level || 'Unknown'))).filter(Boolean);

  const valueDatasets = severities.map((sev, i) => {
    const data = valueDates.map(date => {
      const match = total_estimated_value.find(item => (item.date === date || item.day === date || item.timestamp === date) && ((item.severity || item.threat_level || 'Unknown') === sev));
      return match ? (match.total_value || match.value || match.estimated_value || match.amount || 0) : 0;
    });
    return { label: sev, data, backgroundColor: chartColors[i % chartColors.length] };
  });

  const itemDates = Array.from(new Set(items_stolen.map(item => item.date || item.day || item.timestamp))).filter(Boolean).sort();
  const zones = Array.from(new Set(items_stolen.map(item => item.zone || item.location || 'Unknown'))).filter(Boolean);

  const itemsDatasets = zones.map((zone, i) => {
    const data = itemDates.map(date => {
      const match = items_stolen.find(item => (item.date === date || item.day === date || item.timestamp === date) && ((item.zone || item.location || 'Unknown') === zone));
      return match ? (match.item_count || match.count || match.incidents || match.items || 0) : 0;
    });
    return { label: zone, data, backgroundColor: chartColors[i % chartColors.length] };
  });

  return (
    <Card sx={{ p: 2, height: 'auto', bgcolor: 'background.paper', mb: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
         <Typography variant="h6">High-Value Target Analysis</Typography>
         <DesBtn text="Filter" handle={openFilter}>
          <FilterAltOutlinedIcon />
         </DesBtn>
      </Box>

      {loading || !dataList ? (
        <Skeleton variant="rectangular" height="400px" sx={{ borderRadius: 2 }} />
      ) : (
        <Grid container spacing={3} sx={{ height: 400 }}>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 2, height: '100%', bgcolor: 'background.paper' }}>
              <Typography variant="subtitle1" mb={2}>Total Estimated Value Of Stolen Items ($)</Typography>
              <Box sx={{ height: 320 }}>
                <Bar 
                  data={{
                    labels: valueDates.length ? valueDates : ['No Data'],
                    datasets: valueDatasets.length ? valueDatasets : [{ label: 'Value ($)', data: [0], backgroundColor: chartColors[1] }]
                  }}
                  options={commonOptions}
                />
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 2, height: '100%', bgcolor: 'background.paper' }}>
              <Typography variant="subtitle1" mb={2}>Items Stolen Per Time & Zone</Typography>
              <Box sx={{ height: 320 }}>
                <Bar 
                  data={{
                    labels: itemDates.length ? itemDates : ['No Data'],
                    datasets: itemsDatasets.length ? itemsDatasets : [{ label: 'Items', data: [0], backgroundColor: chartColors[2] }]
                  }}
                  options={commonOptions}
                />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Card>
  );
};

export default HighValueChart;
